import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import Autoplay from "embla-carousel-autoplay";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Wrench,
  Zap,
  Home,
  Paintbrush,
  Hammer,
  CheckCircle,
  ArrowRight,
  Star,
  Shield,
  ShieldCheck,
  Clock,
  Award,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  TreeDeciduous,
  Waves,
  Wind,
  Bug,
  Building2,
  ClipboardCheck,
  Droplets,
  Boxes,
  Truck,
  Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const movePackages = [
  {
    id: "move-in",
    badge: "New Home Ready",
    name: "Move-In Package",
    icon: Boxes,
    description:
      "Walk into a spotless, fully set-up home. We handle the heavy lifting so you can settle in.",
    includes: [
      "Deep cleaning, top to bottom",
      "Furniture assembly",
      "TV, shelf & mirror mounting",
      "Minor repairs & touch-ups",
    ],
  },
  {
    id: "move-out",
    badge: "Deposit Saver",
    name: "Move-Out Package",
    icon: Truck,
    description:
      "Leave the property in great shape and protect your deposit. We take care of every detail.",
    includes: [
      "Move-out deep cleaning",
      "Furniture disassembly & moving help",
      "Patch & paint touch-ups",
      "Final walk-through prep",
    ],
  },
];

const services = [
  {
    id: "plumbing",
    name: "Plumbing",
    description: "Leaks, clogs, installations & repairs",
    icon: Wrench,
    image: "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "electrical",
    name: "Electrical",
    description: "Wiring, outlets, lighting & safety",
    icon: Zap,
    image: "https://images.pexels.com/photos/8488029/pexels-photo-8488029.jpeg",
  },
  {
    id: "roofing",
    name: "Roofing",
    description: "Repairs, maintenance & inspections",
    icon: Home,
    image: "https://images.unsplash.com/photo-1628002580365-f3c0a322d577?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "cleaning",
    name: "Cleaning",
    description: "Deep cleaning, move-in/out & more",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "lawn_care",
    name: "Lawn Care",
    description: "Mowing, landscaping & irrigation",
    icon: TreeDeciduous,
    image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "hvac",
    name: "HVAC",
    description: "Heating, cooling & ventilation",
    icon: Wind,
    image: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "inspection",
    name: "Building Inspection",
    description: "Pre-purchase & post-construction inspections",
    icon: ClipboardCheck,
    image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    id: "ppm",
    name: "Project Payment Management",
    description: "Funds held securely until the work is verified complete",
    icon: ShieldCheck,
    image: "https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&q=85",
    to: "/project-payment-management",
  },
];

const homeServiceTypes = [
  "Plumbing",
  "Electrical",
  "Roofing",
  "Tiling",
  "Carpentry",
  "Painting",
  "Cleaning",
  "Lawn Care",
  "HVAC",
  "Pre-Purchase Building Inspection",
  "Move-In / Move-Out Package",
  "Other (Please describe)",
];

const heroAnnouncements = [
  {
    icon: Sparkles,
    label: "New: Pre-Purchase & Post-Construction Inspections",
    to: "/booking?category=inspection",
  },
  {
    icon: ShieldCheck,
    label: "New: Project Payment Management — funds held securely",
    to: "/project-payment-management",
  },
  {
    icon: Hammer,
    label: "New: ProToCall Labour Registry — get found, get hired",
    to: "/labour-registry",
  },
];

const features = [  {
    icon: Shield,
    title: "Licensed & Insured",
    description: "Full coverage for your peace of mind",
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    description: "One-time or recurring service options",
  },
  {
    icon: ShieldCheck,
    title: "Service Warranty",
    description: "Workmanship covered on every completed job",
  },
];

const faqs = [
  {
    q: "What areas in the Caribbean do you serve?",
    a: "ProToCall is now serving Saint Lucia, with service across the wider OECS — Antigua and Barbuda, Commonwealth of Dominica, Grenada, Montserrat, Saint Kitts and Nevis, Saint Vincent and the Grenadines — plus Barbados launching soon. Contact us to confirm coverage for your specific location.",
  },
  {
    q: "How much is the callout fee?",
    a: "We charge a flat, upfront callout fee starting at XCD $250 — with no hidden charges. The fee may vary slightly by island to account for inter-island travel. You'll always know the price before we arrive.",
  },
  {
    q: "What is a Pre-Purchase Building Inspection?",
    a: "It's a thorough assessment of a property before you buy it. Our certified inspectors check structural integrity, roofing, plumbing, electrical systems and moisture issues, then provide a detailed written report so you can negotiate with confidence and avoid costly surprises.",
  },
  {
    q: "Do you offer recurring maintenance plans?",
    a: "Yes. You can book a one-time service or set up a recurring weekly or monthly maintenance schedule. Just choose your preferred frequency when booking or request a custom quote.",
  },
  {
    q: "Are your handymen licensed and insured?",
    a: "Absolutely. All our professionals are fully licensed and insured, and every completed job is covered by our workmanship warranty for your peace of mind.",
  },
  {
    q: "How do I get a free quote?",
    a: "Click 'Get a Free Quote', describe your project, and optionally drop a GPS pin at your location. We'll review the details and send you a tailored, no-obligation quote.",
  },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [calloutFee, setCalloutFee] = useState(250);
  const { formatMoney } = useCurrency();
  const [reviewStats, setReviewStats] = useState({ average: 0, count: 0 });
  const [featured, setFeatured] = useState([]);
  const [featuredFlipped, setFeaturedFlipped] = useState({});
  const autoplay = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [announcementIdx, setAnnouncementIdx] = useState(0);

  const handleContactChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (
      !contactForm.name ||
      !contactForm.email ||
      !contactForm.phone ||
      !contactForm.service_type ||
      !contactForm.message
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSubmitted(true);
      toast.success("Your message has been sent successfully!");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
    fetchCalloutFee();
    fetchFeatured();
    fetchReviewStats();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnnouncementIdx((i) => (i + 1) % heroAnnouncements.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const fetchReviewStats = async () => {
    try {
      const res = await axios.get(`${API}/reviews/stats`);
      setReviewStats(res.data || { average: 0, count: 0 });
    } catch (error) {
      console.error("Error fetching review stats:", error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      // Prefer approved customer reviews; fall back to seeded testimonials when empty.
      const reviewsRes = await axios.get(`${API}/reviews?limit=8`);
      if (Array.isArray(reviewsRes.data) && reviewsRes.data.length > 0) {
        const mapped = reviewsRes.data.map((r) => ({
          id: r.id,
          name: r.customer_name,
          location: r.customer_location || "Saint Lucia",
          rating: r.rating,
          text: r.comment,
          service: r.service_name || "Service",
          image_url: null,
        }));
        setTestimonials(mapped);
        return;
      }
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const fetchCalloutFee = async () => {
    try {
      const response = await axios.get(`${API}/callout-fee`);
      setCalloutFee(response.data.fee);
    } catch (error) {
      console.error("Error fetching callout fee:", error);
    }
  };

  const fetchFeatured = async () => {
    try {
      const res = await axios.get(`${API}/portfolio/featured?limit=4`);
      setFeatured(res.data || []);
    } catch (error) {
      console.error("Error fetching featured projects:", error);
    }
  };

  const toggleFlip = (id) => {
    setFeaturedFlipped((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "ProToCall",
            url: "https://cariphs.com/",
            potentialAction: {
              "@type": "SearchAction",
              target:
                "https://cariphs.com/services?category={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      {/* Hero Section */}
      <section className="bg-slate-50 pt-6 md:pt-10 lg:pt-14 pb-16 md:pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-6 min-h-9" data-testid="hero-announcement-slider">
                <AnimatePresence mode="wait">
                  {(() => {
                    const a = heroAnnouncements[announcementIdx];
                    const Icon = a.icon;
                    return (
                      <motion.div
                        key={announcementIdx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4 }}
                      >
                        <Link
                          to={a.to}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold transition-transform hover:scale-105"
                          data-testid={`hero-announcement-slide-${announcementIdx}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {a.label}
                          <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                        </Link>
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>
                <div className="flex items-center gap-1.5 mt-3">
                  {heroAnnouncements.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setAnnouncementIdx(i)}
                      aria-label={`Show announcement ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        i === announcementIdx ? "w-6 bg-orange-500" : "w-1.5 bg-orange-200"
                      }`}
                      data-testid={`hero-announcement-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="hero-title"
              >
                Construction and Property
                <span className="text-orange-500"> Services Coordination</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-lg">
                Connecting you with vetted trade professionals across
                Saint Lucia — from plumbing and painting to roofing and renovations. One call
                gets you a vetted pro, a written quote, and guaranteed workmanship.
              </p>
              
              {/* Callout Fee Info */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8 max-w-md">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Only {formatMoney(calloutFee, { decimals: 0 })} Callout Fee</p>
                    <p className="text-sm text-slate-600">Flat, upfront rate — no hidden charges. Great value to get a pro on site.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-12 px-8 rounded-md"
                  data-testid="hero-book-now-btn"
                >
                  <Link to="/booking">
                    Book a Service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white font-semibold h-12 px-8 rounded-md"
                  data-testid="hero-quote-btn"
                >
                  <Link to="/quote">Get a Free Quote</Link>
                </Button>
              </div>
              {/* Trust Badges */}
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-200" data-testid="hero-trust-badges">
                {reviewStats.count > 0 ? (
                  <div className="flex items-center gap-1" data-testid="hero-rating">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${
                          i < Math.round(reviewStats.average)
                            ? "fill-orange-400 text-orange-400"
                            : "text-slate-300"
                        }`}
                      />
                    ))}
                    <span className="ml-2 text-sm text-slate-600 font-medium">
                      {reviewStats.average.toFixed(1)}/5 ({reviewStats.count}{" "}
                      review{reviewStats.count !== 1 ? "s" : ""})
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1" data-testid="hero-rating-empty">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-slate-300" />
                    ))}
                    <span className="ml-2 text-sm text-slate-600 font-medium">
                      Be our first 5-star review!
                    </span>
                  </div>
                )}
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Locally</span> Owned & Operated
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
              data-testid="home-contact-form-wrapper"
            >
              <Card className="bg-white border border-slate-200 shadow-2xl rounded-2xl">
                <CardContent className="p-6 sm:p-8">
                  <div className="mb-6">
                    <span className="inline-flex items-center gap-2 text-orange-500 text-sm font-semibold uppercase tracking-wider">
                      <Building2 className="w-4 h-4" />
                      Residential & Commercial Services
                    </span>
                    <h2
                      className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      Get in Touch
                    </h2>
                    <p className="text-slate-600 text-sm mt-2">
                      Tell us what you need and we'll get back to you within 24 hours.
                    </p>
                  </div>

                  {contactSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className="text-center py-10"
                      data-testid="home-contact-success"
                    >
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      </div>
                      <h3
                        className="text-xl font-bold text-slate-900 mb-2"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        Message Sent!
                      </h3>
                      <p className="text-slate-600 text-sm mb-6">
                        Thanks for reaching out. We'll be in touch within 24 hours.
                      </p>
                      <Button
                        onClick={() => {
                          setContactSubmitted(false);
                          setContactForm({
                            name: "",
                            email: "",
                            phone: "",
                            service_type: "",
                            message: "",
                          });
                        }}
                        variant="outline"
                        className="border-2 border-slate-900 rounded-full"
                        data-testid="home-contact-send-another-btn"
                      >
                        Send Another Message
                      </Button>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="home-contact-form">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="home-name" className="text-slate-700 mb-1.5 block text-sm">
                            Full Name *
                          </Label>
                          <Input
                            id="home-name"
                            value={contactForm.name}
                            onChange={(e) => handleContactChange("name", e.target.value)}
                            placeholder="John Doe"
                            className="h-11"
                            data-testid="home-contact-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="home-phone" className="text-slate-700 mb-1.5 block text-sm">
                            Phone *
                          </Label>
                          <Input
                            id="home-phone"
                            value={contactForm.phone}
                            onChange={(e) => handleContactChange("phone", e.target.value)}
                            placeholder="(758) 123-4567"
                            className="h-11"
                            data-testid="home-contact-phone-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="home-email" className="text-slate-700 mb-1.5 block text-sm">
                          Email Address *
                        </Label>
                        <Input
                          id="home-email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => handleContactChange("email", e.target.value)}
                          placeholder="john@example.com"
                          className="h-11"
                          data-testid="home-contact-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="home-service" className="text-slate-700 mb-1.5 block text-sm">
                          Service Needed *
                        </Label>
                        <Select
                          value={contactForm.service_type}
                          onValueChange={(value) => handleContactChange("service_type", value)}
                        >
                          <SelectTrigger className="h-11" data-testid="home-contact-service-select">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {homeServiceTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="home-message" className="text-slate-700 mb-1.5 block text-sm">
                          Message *
                        </Label>
                        <Textarea
                          id="home-message"
                          value={contactForm.message}
                          onChange={(e) => handleContactChange("message", e.target.value)}
                          placeholder="Briefly describe what you need..."
                          className="min-h-[90px]"
                          data-testid="home-contact-message-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={contactLoading}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 rounded-full"
                        data-testid="home-contact-submit-btn"
                      >
                        {contactLoading ? (
                          <>
                            <span className="animate-spin mr-2">⌛</span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 w-4 h-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Service Spotlight — Pre-Purchase Building Inspection */}
      <section className="py-12 md:py-16 bg-white" data-testid="inspection-spotlight-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-900 rounded-3xl overflow-hidden relative text-white flex flex-col md:flex-row shadow-2xl"
          >
            <div className="p-10 md:p-16 flex-1 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-5 w-fit">
                <Building2 className="w-4 h-4" />
                New Service
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Pre-Purchase Building Inspection
              </h2>
              <p className="text-slate-300 leading-relaxed mb-8 max-w-lg">
                Buying a property in Saint Lucia? Don't take a costly gamble. Our certified
                inspectors assess structural integrity, roofing, plumbing, electrical and moisture
                issues — so you negotiate with confidence and avoid expensive surprises.
              </p>
              <ul className="grid sm:grid-cols-2 gap-3 mb-9">
                {[
                  "Full structural assessment",
                  "Roof, plumbing & electrical checks",
                  "Moisture & damage detection",
                  "Detailed written report",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-200">
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8 rounded-full shadow-lg"
                  data-testid="inspection-book-btn"
                >
                  <Link to="/booking?category=inspection">
                    Book Inspection
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white hover:text-slate-900 font-semibold h-12 px-8 rounded-full bg-transparent"
                  data-testid="inspection-quote-btn"
                >
                  <Link to="/quote?category=inspection">Get a Free Quote</Link>
                </Button>
              </div>
            </div>
            <div className="flex-1 relative min-h-[300px]">
              <img
                src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Professional building inspector evaluating a property"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="inspection-spotlight-image"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/40 to-transparent md:bg-gradient-to-l" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 rounded-xl bg-slate-50"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <h3
                    className="font-semibold text-slate-900 mb-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
              What We Do
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Our Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive property maintenance for homes and businesses. 
              One-time service or set up recurring maintenance schedules.
            </p>
          </div>
          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[autoplay.current]}
            className="w-full px-2 sm:px-12"
            data-testid="services-carousel"
          >
            <CarouselContent className="-ml-4">
              {services.map((service) => (
                <CarouselItem
                  key={service.id}
                  className="pl-4 basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <Card
                    className="service-card bg-white border border-slate-200 overflow-hidden group h-full flex flex-col"
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-slate-900/50 transition-colors" />
                      <div className="absolute top-4 left-4 w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-lg">
                        <service.icon className="w-5 h-5 text-orange-500" />
                      </div>
                    </div>
                    <CardContent className="p-6 flex flex-col flex-grow">
                      <h3
                        className="font-semibold text-lg text-slate-900 mb-2"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {service.name}
                      </h3>
                      <p className="text-slate-600 text-sm mb-6">{service.description}</p>
                      <div className="mt-auto flex gap-3">
                        {service.to ? (
                          <Button
                            asChild
                            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full"
                            data-testid={`learn-more-${service.id}`}
                          >
                            <Link to={service.to}>Learn More</Link>
                          </Button>
                        ) : (
                          <>
                            <Button
                              asChild
                              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full"
                              data-testid={`book-now-${service.id}`}
                            >
                              <Link to={`/booking?category=${service.id}`}>Book Now</Link>
                            </Button>
                            <Button
                              asChild
                              variant="outline"
                              className="flex-1 border-2 border-slate-200 hover:border-slate-900 text-slate-900 font-semibold rounded-full bg-transparent"
                              data-testid={`learn-more-${service.id}`}
                            >
                              <Link to={`/services?category=${service.id}`}>Learn More</Link>
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              className="hidden sm:flex border-slate-300 left-0 lg:left-1"
              data-testid="services-carousel-prev"
            />
            <CarouselNext
              className="hidden sm:flex border-slate-300 right-0 lg:right-1"
              data-testid="services-carousel-next"
            />
          </Carousel>
          <div className="text-center mt-12">
            <Button
              asChild
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8 rounded-md"
              data-testid="view-all-services-btn"
            >
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Move-In / Move-Out Packages Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="move-packages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
              Bundle & Save Time
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Move-In & Move-Out Packages
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Moving across the islands? Let one trusted crew handle it all. Tell us your dates and
              we'll send a free, tailored quote.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {movePackages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  className="h-full bg-slate-50 border border-slate-200 overflow-hidden flex flex-col"
                  data-testid={`move-package-${pkg.id}`}
                >
                  <CardContent className="p-8 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                        <pkg.icon className="w-7 h-7 text-orange-500" />
                      </div>
                      <span className="bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                        {pkg.badge}
                      </span>
                    </div>
                    <h3
                      className="font-bold text-2xl text-slate-900 mb-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {pkg.name}
                    </h3>
                    <p className="text-slate-600 mb-6">{pkg.description}</p>
                    <ul className="space-y-3 mb-8">
                      {pkg.includes.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-slate-700">
                          <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto">
                      <Button
                        asChild
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 rounded-md"
                        data-testid={`move-package-quote-btn-${pkg.id}`}
                      >
                        <Link to={`/quote?package=${pkg.id}`}>
                          Get a Free Quote
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      {featured.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="featured-projects-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
              <div>
                <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
                  Featured Work
                </span>
                <h2
                  className="text-3xl md:text-4xl font-bold text-slate-900 mt-3"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Recent Project Transformations
                </h2>
                <p className="text-slate-600 mt-2 max-w-2xl">
                  Real before/after work from our crews across Saint Lucia. Tap any image to flip.
                </p>
              </div>
              <Button
                asChild
                variant="outline"
                className="border-slate-300 self-start md:self-auto"
                data-testid="featured-view-all-btn"
              >
                <Link to="/portfolio">
                  View Full Portfolio
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((project, index) => {
                const flipped = !!featuredFlipped[project.id];
                const imgSrc = flipped
                  ? `${process.env.REACT_APP_BACKEND_URL}${project.after_url}`
                  : `${process.env.REACT_APP_BACKEND_URL}${project.before_url}`;
                return (
                  <motion.button
                    key={project.id}
                    type="button"
                    onClick={() => toggleFlip(project.id)}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: index * 0.08 }}
                    className="group relative text-left rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid={`featured-project-${project.id}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={imgSrc}
                        alt={`${project.service_category} project`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3">
                        <span
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full shadow ${
                            flipped
                              ? "bg-green-500 text-white"
                              : "bg-white text-slate-900"
                          }`}
                        >
                          {flipped ? "AFTER" : "BEFORE"}
                        </span>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-900/80 to-transparent p-4">
                        <p className="text-white text-sm font-semibold capitalize">
                          {project.service_category.replace("_", " ")}
                        </p>
                        <p className="text-slate-200 text-xs">Tap to {flipped ? "see before" : "reveal after"}</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
                Testimonials
              </span>
              <h2
                className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                What Our Clients Say
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="bg-slate-50 rounded-2xl p-8 md:p-10 border-l-4 border-orange-500"
                  data-testid={`testimonial-${currentTestimonial}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonials[currentTestimonial]?.rating || 5)].map(
                      (_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-orange-400 text-orange-400"
                        />
                      )
                    )}
                  </div>
                  <p className="text-slate-700 text-lg leading-relaxed mb-6">
                    "{testimonials[currentTestimonial]?.text}"
                  </p>
                  <div className="flex items-center gap-4">
                    {testimonials[currentTestimonial]?.image_url && (
                      <img
                        src={testimonials[currentTestimonial].image_url}
                        alt={testimonials[currentTestimonial].name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">
                        {testimonials[currentTestimonial]?.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {testimonials[currentTestimonial]?.location} •{" "}
                        {testimonials[currentTestimonial]?.service}
                      </p>
                    </div>
                  </div>
                </motion.div>
                <div className="flex items-center justify-center gap-4 mt-8">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={prevTestimonial}
                    className="rounded-full"
                    data-testid="prev-testimonial-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-colors ${
                          index === currentTestimonial
                            ? "bg-orange-500"
                            : "bg-slate-300"
                        }`}
                        data-testid={`testimonial-dot-${index}`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={nextTestimonial}
                    className="rounded-full"
                    data-testid="next-testimonial-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">
              Got Questions?
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mt-3 mb-4"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600">
              Everything you need to know about our services, pricing and coverage.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border-b border-slate-200"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger
                  className="text-base md:text-lg font-semibold text-slate-900 py-5 hover:no-underline"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Ready to Get Started?
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-10">
              Book your service today. Set up recurring maintenance or request a one-time service.
              Just a flat XCD ${calloutFee} callout fee — no surprises.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8 rounded-md"
                data-testid="cta-book-btn"
              >
                <Link to="/register">
                  Create Account
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold h-12 px-8 rounded-md"
                data-testid="cta-quote-btn"
              >
                <Link to="/quote">Get a Free Quote</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
